import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { contentService } from '../api/services/content';
import { topicsService, Topic, Subtopic } from '../api/services/topics';
import {
  APIResponse,
  ContentItem,
  ContentListParams,
  MCQPayload,
  TrueFalsePayload,
  SelectMultiplePayload,
  ExplanationPayload,
  CommonMistakePayload,
  ExampleQAPayload,
  ContentType,
  ContentTypeLabels,
  ContentDifficulty,
  DIFFICULTY_DISPLAY_NAMES,
} from '@memriti/shared';
import Container from '../components/common/Container';
import ByteLearningView from '../components/ByteLearning/ByteLearningView';
import SafeHtmlRenderer from '../components/common/SafeHtmlRenderer';

const TABS = [
  { key: 'questions', label: 'Practice Questions' },
  { key: 'bytes', label: 'Learning Bytes' },
];

const LearningResources: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [subtopics, setSubtopics] = useState<Subtopic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState<Subtopic | null>(null);
  const [questions, setQuestions] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isLoadingSubtopics, setIsLoadingSubtopics] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [revealedAnswers, setRevealedAnswers] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [questionsPerPage, setQuestionsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState<'bytes' | 'questions'>('questions');
  const [bytes, setBytes] = useState<ContentItem[]>([]);
  const [isLoadingBytes, setIsLoadingBytes] = useState(false);

  const difficultyOrder = [
    ContentDifficulty.VERY_EASY,
    ContentDifficulty.EASY,
    ContentDifficulty.MODERATE,
    ContentDifficulty.DIFFICULT,
    ContentDifficulty.VERY_DIFFICULT,
  ];

  function sortByDifficulty(a: ContentItem, b: ContentItem) {
    return difficultyOrder.indexOf(a.difficulty) - difficultyOrder.indexOf(b.difficulty);
  }

  // Sort questions by difficulty and apply pagination
  const sortedQuestions = questions.sort(sortByDifficulty);
  const totalPages = Math.ceil(sortedQuestions.length / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = sortedQuestions.slice(startIndex, endIndex);



  // Fetch topics on component mount
  useEffect(() => {
    const fetchTopics = async () => {
      if (!user) {
        setError('Please log in to access learning resources');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await topicsService.getTopics();
        setTopics(response.data);
      } catch (err: any) {
        if (err.response?.status === 401) {
          setError('Please log in to access learning resources');
        } else {
          setError('Failed to load topics');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (!authLoading) {
      fetchTopics();
    }
  }, [user, authLoading]);

  // Fetch subtopics when topic is selected
  useEffect(() => {
    if (!selectedTopic) {
      setSubtopics([]);
      setSelectedSubtopic(null);
      return;
    }

    const fetchSubtopics = async () => {
      if (!user) {
        setError('Please log in to access learning resources');
        return;
      }

      try {
        setIsLoadingSubtopics(true);
        setError(null);
        const response = await topicsService.getSubtopics(selectedTopic.id);
        setSubtopics(response.data);
      } catch (err: any) {
        if (err.response?.status === 401) {
          setError('Please log in to access learning resources');
        } else {
          setError('Failed to load subtopics');
        }
      } finally {
        setIsLoadingSubtopics(false);
      }
    };

    fetchSubtopics();
  }, [selectedTopic, user]);

  // Fetch questions when subtopic is selected
  useEffect(() => {
    if (!selectedSubtopic) {
      setQuestions([]);
      setTotalQuestions(0);
      return;
    }

    const fetchQuestions = async () => {
      if (!user) {
        setError('Please log in to access learning resources');
        return;
      }

      try {
        setIsLoadingQuestions(true);
        setError(null);

        const params: ContentListParams = {
          page: 1,
          per_page: 100, // Fetch more questions to enable proper difficulty sorting
          subtopic_ids: [selectedSubtopic.id],
          content_types: [ContentType.MCQ],
        };

        const response = await contentService.listContent(params);
        setQuestions(response.data.items);
        setTotalQuestions(response.data.total);
      } catch (err: any) {
        if (err.response?.status === 401) {
          setError('Please log in to access learning resources');
        } else {
          setError('Failed to load questions');
        }
      } finally {
        setIsLoadingQuestions(false);
      }
    };

    fetchQuestions();
  }, [selectedSubtopic, user]);

  // Fetch bytes when subtopic changes or bytes are empty
  useEffect(() => {
    if (activeTab !== 'bytes' || !selectedSubtopic) {
      return;
    }
    if (bytes.length > 0) {
      // Already loaded, do not fetch again
      return;
    }
    const fetchBytes = async () => {
      setIsLoadingBytes(true);
      try {
        const params: ContentListParams = {
          subtopic_ids: [selectedSubtopic.id],
          content_types: [
            ContentType.EXPLANATION,
            ContentType.COMMON_MISTAKES,
            ContentType.TIPS_TRICKS,
          ],
          per_page: 100, // fetch enough for swipe experience
        };
        const response = await contentService.listContent(params);
        setBytes(response.data.items);
      } catch (err) {
        setBytes([]);
      } finally {
        setIsLoadingBytes(false);
      }
    };
    fetchBytes();
  }, [activeTab, selectedSubtopic]);

  // Clear bytes only when subtopic changes
  useEffect(() => {
    setBytes([]);
  }, [selectedSubtopic]);

  // Filter bytes by content type in difficulty order
  const filteredBytes = useMemo(() => {
    if (!bytes) return [];

    const explanationContent = bytes
      .filter(item => item.type === ContentType.EXPLANATION)
      .sort(sortByDifficulty);

    const commonMistakesContent = bytes
      .filter(item => item.type === ContentType.COMMON_MISTAKES)
      .sort(sortByDifficulty);

    const tipsTricksContent = bytes
      .filter(item => item.type === ContentType.TIPS_TRICKS)
      .sort(sortByDifficulty);

    // Return in difficulty order: explanations (easiest) -> common mistakes (medium) -> tips & tricks (advanced)
    return [
      ...explanationContent,
      ...commonMistakesContent,
      ...tipsTricksContent,
    ];
  }, [bytes]);

  const handleTopicSelect = (topic: Topic) => {
    setSelectedTopic(topic);
    setSelectedSubtopic(null);
    setQuestions([]);
    setCurrentPage(1);
    setRevealedAnswers(new Set());
  };

  const handleSubtopicSelect = (subtopic: Subtopic) => {
    setSelectedSubtopic(subtopic);
    setQuestions([]);
    setCurrentPage(1);
    setRevealedAnswers(new Set());
  };

  const handleShowAnswer = (questionId: number) => {
    setRevealedAnswers(prev => new Set([...Array.from(prev), questionId]));
  };



  const renderQuestion = (question: ContentItem) => {
    const getQuestionText = () => {
      if (question.type === ContentType.MCQ) {
        const payload = question.payload as MCQPayload;
        return payload.question;
      } else if (question.type === ContentType.TRUE_FALSE) {
        const payload = question.payload as TrueFalsePayload;
        return payload.statement;
      }
      return '';
    };

    const getOptions = () => {
      if (question.type === ContentType.MCQ) {
        const payload = question.payload as MCQPayload;
        return payload.options;
      }
      return [];
    };

    const getCorrectAnswer = () => {
      if (question.type === ContentType.MCQ) {
        const payload = question.payload as MCQPayload;
        return payload.correct_answer;
      } else if (question.type === ContentType.TRUE_FALSE) {
        const payload = question.payload as TrueFalsePayload;
        return payload.answer.toString();
      }
      return '';
    };

    const getExplanation = () => {
      if (question.type === ContentType.MCQ) {
        const payload = question.payload as MCQPayload;
        return payload.explanation;
      } else if (question.type === ContentType.TRUE_FALSE) {
        const payload = question.payload as TrueFalsePayload;
        return payload.explanation;
      }
      return '';
    };

    const getDifficultyColor = (difficulty: string) => {
      switch (difficulty) {
        case 'very_easy':
          return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
        case 'easy':
          return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200';
        case 'moderate':
          return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
        case 'difficult':
          return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200';
        case 'very_difficult':
          return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
        default:
          return 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200';
      }
    };

    const getDifficultyDisplayName = (difficulty: string) => {
      const difficultyEnum = difficulty as ContentDifficulty;
      return DIFFICULTY_DISPLAY_NAMES[difficultyEnum] || difficulty;
    };

    const isAnswerRevealed = revealedAnswers.has(question.id);

    return (
      <div key={question.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 max-w-2xl mx-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {ContentTypeLabels[question.type as ContentType]}
            </span>
            {question.difficulty && (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(question.difficulty)}`}>
                {getDifficultyDisplayName(question.difficulty)}
              </span>
            )}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 leading-relaxed">
            <SafeHtmlRenderer html={getQuestionText()} />
          </h3>

          {question.type !== ContentType.TRUE_FALSE && (
            <div className="space-y-3">
              {getOptions().map((option, index) => {
                const correctAnswer = getCorrectAnswer();
                const isCorrect = Array.isArray(correctAnswer)
                  ? correctAnswer.includes(option)
                  : correctAnswer === option;

                return (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border transition-colors ${
                      isAnswerRevealed && isCorrect
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-900 dark:text-white">
                        <SafeHtmlRenderer html={option} />
                      </span>
                      {isAnswerRevealed && isCorrect && (
                        <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                          ✓ Correct
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {question.type === ContentType.TRUE_FALSE && (
            <div className="space-y-3">
              <div className={`p-3 rounded-lg border transition-colors ${
                isAnswerRevealed && (question.payload as TrueFalsePayload).answer === true
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                  : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">True</span>
                  {isAnswerRevealed && (question.payload as TrueFalsePayload).answer === true && (
                    <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                      ✓ Correct
                    </span>
                  )}
                </div>
              </div>
              <div className={`p-3 rounded-lg border transition-colors ${
                isAnswerRevealed && (question.payload as TrueFalsePayload).answer === false
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                  : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">False</span>
                  {isAnswerRevealed && (question.payload as TrueFalsePayload).answer === false && (
                    <span className="text-green-600 dark:text-green-400 text-sm font-medium">
                      ✓ Correct
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {!isAnswerRevealed ? (
          <button
            onClick={() => handleShowAnswer(question.id)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
          >
            Show Answer
          </button>
        ) : (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="text-blue-800 dark:text-blue-200 text-sm leading-relaxed">
              <SafeHtmlRenderer html={getExplanation() || 'No explanation available.'} />
            </div>
          </div>
        )}
      </div>
    );
  };

  if (authLoading) {
    return (
      <Container className="px-4 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-6 text-center">
            <div className="text-red-600 dark:text-red-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-red-900 dark:text-red-100 mb-2">Error</h3>
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container className="px-4 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading topics...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container className="px-4 py-6 min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Learning Resources</h1>
              <p className="text-gray-600 dark:text-gray-300">Explore learning bytes and practice questions by topic</p>
            </div>
          </div>

          {/* Topic Selection Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-nowrap overflow-x-auto whitespace-nowrap gap-2 -mx-2 px-1">
              {topics.map(topic => (
                <button
                  key={topic.id}
                  onClick={() => handleTopicSelect(topic)}
                  className={`px-4 py-2 mx-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedTopic?.id === topic.id
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {topic.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col space-y-6">
          {selectedTopic ? (
            selectedSubtopic ? (
              <div className="space-y-6">
                {/* Subtopic Header with Back Button */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          setSelectedSubtopic(null);
                          setQuestions([]);
                          setCurrentPage(1);
                          setRevealedAnswers(new Set());
                        }}
                        className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {selectedSubtopic.name}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {selectedTopic.name} • Learning Resources
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex justify-center mb-6">
                  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 p-1">
                    <button
                      onClick={() => setActiveTab('questions')}
                      className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                        activeTab === 'questions'
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
                      }`}
                    >
                      Questions
                    </button>
                    <button
                      onClick={() => setActiveTab('bytes')}
                      className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                        activeTab === 'bytes'
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
                      }`}
                    >
                      Learning Bytes
                    </button>
                  </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'questions' ? (
                  <>
                    {isLoadingQuestions ? (
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 max-w-2xl mx-auto">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading questions...</span>
                        </div>
                      </div>
                    ) : questions.length > 0 ? (
                      <div className="space-y-4 max-w-2xl mx-auto">
                        {currentQuestions.map(renderQuestion)}

                        {/* Frontend Pagination */}
                        {totalPages > 1 && (
                          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                  disabled={currentPage === 1}
                                  className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Previous
                                </button>
                                <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                  Page {currentPage} of {totalPages}
                                </span>
                                <button
                                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                  disabled={currentPage >= totalPages}
                                  className="px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Next
                                </button>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-2">
                                  <label htmlFor="page-size" className="text-sm text-gray-500 dark:text-gray-400">
                                    Show:
                                  </label>
                                  <select
                                    id="page-size"
                                    value={questionsPerPage}
                                    onChange={(e) => {
                                      setQuestionsPerPage(Number(e.target.value));
                                      setCurrentPage(1); // Reset to first page when changing page size
                                    }}
                                    className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                  </select>
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {startIndex + 1}-{Math.min(endIndex, questions.length)} of {questions.length}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center max-w-2xl mx-auto">
                        <div className="text-gray-400 dark:text-gray-500 mb-4">
                          <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No questions found</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          No MCQ questions available for this subtopic yet.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full min-h-[400px] flex flex-col">
                    {isLoadingBytes ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : (
                      <div className="flex-1 w-full h-full">
                        <ByteLearningView content={filteredBytes} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {/* Subtopic Selection Grid */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {selectedTopic.name}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300">Select a subtopic to explore learning content and practice questions</p>
                  </div>

                  {isLoadingSubtopics ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                      <span className="ml-3 text-gray-600 dark:text-gray-400">Loading subtopics...</span>
                    </div>
                  ) : subtopics.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {subtopics.map(subtopic => (
                        <button
                          key={subtopic.id}
                          onClick={() => handleSubtopicSelect(subtopic)}
                          className="p-4 text-left bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-all duration-200 group"
                        >
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
                              {subtopic.name}
                            </h3>
                            <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                          {subtopic.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {subtopic.description}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-400 dark:text-gray-500 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No subtopics available</h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        This topic doesn't have any subtopics yet.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Select a topic to get started</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Choose a topic from the tabs above to browse available learning resources and start practicing.
              </p>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default LearningResources;